import { collectWriteIntents, type PortWithWrites } from './support-b3';
import { strtoupper, strtolower } from './support-b3';
import { type Row, type PortEnv, type PhpRef, get, values, entries, isArray, intval } from './support-b3';
interface Deps {
    inventory_field(row: Row, key: string): unknown;
    encounter_field(row: Row, key: string): unknown;
    check_uuid(id: string): boolean;
    janus_item_layers_for_ability_charge_rules(row: Row): unknown;
    layer_value(row: unknown, key: string): unknown;
    item_field(row: unknown, key: string): unknown;
    creature_statuses(row: Row, role: string): unknown;
    apply_status_effect(connected: string, caster: Row, target: PhpRef<Row>, targets: string, affected: PhpRef<Row>, origin: string, item: string, ends: number, rollId: string, log: boolean, message: string, private_: number, overlay: null, aoeSource: number, opposing: number, paused: null, hasDuration: boolean, bypass: boolean, campaign: Row | null, encounter: Row | null, template: null, duplicates: boolean, suppress: boolean): boolean;
    update_status_effect_stats(row: unknown, updates: Row, affected: PhpRef<Row>): unknown;
    ft_status_target_unset(target: PhpRef<Row>, key: unknown): unknown;
}
/** PHP: janus_contest_success_immunity_marker (includes/functions/janus/statuses/contest_success_immunity.php:5) */
export function janusContestSuccessImmunityMarker(): string { return '__janus_contest_success_immunity_until_long_rest__'; }
/** PHP: janus_contest_success_immunity_status_definition (includes/functions/janus/statuses/contest_success_immunity.php:11) */
export function janusContestSuccessImmunityStatusDefinition(id: string): Row { return { items: { item_id: id, item_name: 'Contest Success Immunity', item_type: 'status_effect' }, item_layer_types: [] }; }
/** PHP: janus_contest_success_immunity_anchor_id (includes/functions/janus/statuses/contest_success_immunity.php:23) */
export function janusContestSuccessImmunityAnchorId(row: Row, env: PortEnv<Deps>): string {
    const inv = env.deps.inventory_field(row, 'inventory_id');
    if (typeof inv === 'string' && env.deps.check_uuid(inv))
        return inv;
    const ec = get(row, 'encounter_creatures', 'encounter_creature_id') ?? '';
    if (typeof ec === 'string' && env.deps.check_uuid(ec))
        return ec;
    const enc = env.deps.encounter_field(row, 'encounter_id');
    if (typeof enc === 'string' && env.deps.check_uuid(enc))
        return enc;
    const status = get(row, 'status_id') ?? '';
    return typeof status === 'string' && env.deps.check_uuid(status) ? status : '';
}
/** PHP: janus_contest_success_immunity_ability_ids (includes/functions/janus/statuses/contest_success_immunity.php:45) */
export function janusContestSuccessImmunityAbilityIds(item: Row, env: PortEnv<Deps>): string[] {
    const layers = [item, ...values(env.deps.janus_item_layers_for_ability_charge_rules(item))], ids = new Map<string, string>();
    for (const layer of layers) {
        if (!isArray(layer) || intval(env.deps.layer_value(layer, 'immune_to_ability_upon_successful_contesting_roll_till_long_rest') ?? 0) !== 1)
            continue;
        const id = env.deps.item_field(layer, 'item_id') ?? get(layer, 'item_id_used', 0) ?? '';
        if (typeof id === 'string' && env.deps.check_uuid(id))
            ids.set(strtoupper(id), id);
    }
    return [...ids.values()];
}
/** PHP: janus_is_contest_success_immunity_status (includes/functions/janus/statuses/contest_success_immunity.php:70) */
export function janusIsContestSuccessImmunityStatus(status: Row): boolean {
    let marker = get(status, 'status_casting_targets_arr') ?? '';
    if (isArray(marker))
        marker = get(marker, 0) ?? '';
    return marker === janusContestSuccessImmunityMarker();
}
/** PHP: janus_target_has_contest_success_immunity_for_ability (includes/functions/janus/statuses/contest_success_immunity.php:79) */
export function janusTargetHasContestSuccessImmunityForAbility(caster: Row, target: Row, id: string, env: PortEnv<Deps>): boolean {
    const casterId = janusContestSuccessImmunityAnchorId(caster, env);
    if (casterId === '' || !env.deps.check_uuid(id))
        return false;
    for (const status of values(env.deps.creature_statuses(target, 'target'))) {
        if (!isArray(status) || !janusIsContestSuccessImmunityStatus(status))
            continue;
        let source = get(status, 'status_caster_id') ?? '';
        if (isArray(source))
            source = get(source, 0) ?? '';
        if (typeof source !== 'string' || strtolower(source) !== strtolower(casterId))
            continue;
        let ability = env.deps.item_field(status, 'item_id') ?? (get(status, 'status_item_id') ?? '');
        if (isArray(ability))
            ability = get(ability, 0) ?? '';
        if (typeof ability === 'string' && strtolower(ability) === strtolower(id))
            return true;
    }
    return false;
}
/** PHP: janus_target_contest_success_immunity_ability_id (includes/functions/janus/statuses/contest_success_immunity.php:116) */
export function janusTargetContestSuccessImmunityAbilityId(caster: Row, target: Row, item: Row, env: PortEnv<Deps>): string | null {
    for (const id of janusContestSuccessImmunityAbilityIds(item, env))
        if (janusTargetHasContestSuccessImmunityForAbility(caster, target, id, env))
            return id;
    return null;
}
/** PHP: janus_record_contest_success_immunity_until_long_rest (includes/functions/janus/statuses/contest_success_immunity.php:129) */
export function janusRecordContestSuccessImmunityUntilLongRest(caster: Row, target: PhpRef<Row>, item: Row, affected: PhpRef<Row>, rollId: string, campaign: Row | null = null, encounter: Row | null = null, env: PortEnv<Deps>): (number) | PortWithWrites<number> {
    return collectWriteIntents(env, env => {
        let applied = 0;
        for (const id of janusContestSuccessImmunityAbilityIds(item, env)) {
            if (janusTargetHasContestSuccessImmunityForAbility(caster, target.value, id, env))
                continue;
            if (env.deps.apply_status_effect(env.uuid(), caster, target, janusContestSuccessImmunityMarker(), affected, id, id, 0, rollId, false, 'status_effect_applied', 1, null, 0, 0, null, false, true, campaign, encounter, null, true, true))
                applied++;
        }
        return applied;
    });
}
/** PHP: janus_clear_contest_success_immunities_on_long_rest (includes/functions/janus/statuses/contest_success_immunity.php:168) */
export function janusClearContestSuccessImmunitiesOnLongRest(target: PhpRef<Row>, affected: PhpRef<Row>, env: PortEnv<Deps>): (number) | PortWithWrites<number> {
    return collectWriteIntents(env, env => {
        let removed = 0;
        for (const [key, row] of entries(env.deps.creature_statuses(target.value, 'target'))) {
            if (!isArray(row) || !janusIsContestSuccessImmunityStatus(row))
                continue;
            env.deps.update_status_effect_stats(row, { status_deleted: 1 }, affected);
            env.deps.ft_status_target_unset(target, key);
            removed++;
        }
        return removed;
    });
}
